import React, { memo } from "react";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { useSortableData } from "../../dashboard-util/dashboard-utils";
import {
  FluentProvider,
  webLightTheme,
} from "@fluentui/react-components";
import { FluentTooltip } from "../../../../components/FluentUI/Fluent-Tooltip";
import PriorityNew from "../../../tasks/scenes/telerik-tasks/common-components/priority-new";
import StatusNew from "../../../tasks/scenes/telerik-tasks/common-components/status-new";
import ArrowDown from "../../../../assets/icons/ArrowDown.svg";
import ArrowHover from "../../../../assets/icons/ArrowHover.svg";
import ArrowUp from "../../../../assets/icons/ArrowUp.svg";
import "../work-load-widget/work-load.scss";

const TasksModalBase = memo(({ show, onHide, title, subtitle, tasks }) => {
  const history = useHistory();
  const { items, requestSort, getArrowDirectionFor } = useSortableData(tasks);

  const tableHeaders = [
    { text: "STR ID", sortKey: "STRID" },
    { text: "Alert Date", sortKey: "AlertDate" },
    { text: "Owner Name", sortKey: "OwnerName" },
    { text: "Trigger Type", sortKey: "TriggerType" },
    { text: "Trigger Source", sortKey: "TriggerSource" },
    { text: "Status", sortKey: "Status" },
    { text: "Priority", sortKey: "Priority" },
    { text: "# of Days Active", sortKey: "NumberOfActiveDays" },
  ];

  const handleRowClick = (taskId) => {
    if (!taskId) return;
    history.push(`/tasks/edit/std/${taskId}`);
  };

  const renderWithTooltip = (text) => (
    <FluentTooltip
      target={<span className="text-ellipsis">{text}</span>}
      text={text}
    />
  );

  const renderSTRIDWithTooltip = (task) => (
    <FluentTooltip
      text={task.STRID}
      target={
        <span
          role="button"
          tabIndex="0"
          onClick={() => handleRowClick(task.TaskId)}
          onKeyDown={(e) => e.key === "Enter" && handleRowClick(task.TaskId)}
          className="clickable-strid text-ellipsis"
        >
          {task.STRID}
        </span>
      }
    />
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      contentClassName="dashboard-modal-styles"
      dialogClassName="dashboard-modal"
      scrollable
      data-testid="tasks-modal-base"
    >
      <Modal.Header className="dashboard-modal-header" closeButton>
        <Modal.Title className="dashboard-modal-title ml-4">
          {title}
          {subtitle && <p className="subDepartmentTitle">{subtitle}</p>}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="workloadModalBody">
        <table className="WorkloadDashboard no-border-table equal-column-width">
          <thead className="MemberTableColumnHeader workloadHeader">
            <tr
              className="tableColumnTitles tableHeader"
              style={{ fontWeight: "300" }}
            >
              {tableHeaders.map(({ text, sortKey }, index) => (
                <th
                  key={sortKey}
                  className="workloadheaderSorted th-hover textEllipsis"
                  style={{
                    textAlign: index === 0 ? "left" : "center",
                    padding: index === 0 ? "10px 20px 10px 23px" : "10px",
                    cursor: "pointer",
                    position: "relative",
                  }}
                  onClick={() => requestSort(sortKey)}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <p className="textSorted">{text}</p>
                    {getArrowDirectionFor(sortKey, "ascending") && (
                      <img className="arrowDirection" alt="Up" src={ArrowUp} />
                    )}
                    {getArrowDirectionFor(sortKey, "descending") && (
                      <img
                        className="arrowDirection"
                        alt="Down"
                        src={ArrowDown}
                      />
                    )}
                    {getArrowDirectionFor(sortKey) && (
                      <img
                        className="arrowDirectionHover"
                        alt="Hover"
                        src={ArrowHover}
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="tableBody workloadBody">
            {items?.length > 0 ? (
              items.map((task, idx) => (
                <tr key={`${task.TaskId}-${idx}`}>
                  <td className="left-aligned-cell">
                    {task.STRID ? renderSTRIDWithTooltip(task) : ""}
                  </td>
                  <td>
                    {task.AlertDate
                      ? new Date(task.AlertDate).toLocaleDateString()
                      : ""}
                  </td>
                  <td>
                    {renderWithTooltip(task.OwnerName)}
                  </td>
                  <td>{renderWithTooltip(task.TriggerType)}</td>
                  <td>{renderWithTooltip(task.TriggerSource)}</td>
                  <td className="td-workload-status center-workload-status">
                    <FluentProvider theme={webLightTheme}>
                      <StatusNew status={task.Status} />
                    </FluentProvider>
                  </td>
                  <td>
                    <PriorityNew priority={task.Priority} />
                  </td>
                  <td>{task.NumberOfActiveDays}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" style={{ textAlign: "center" }}>
                  No Actionable STRs
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Modal.Body>
    </Modal>
  );
});

TasksModalBase.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  tasks: PropTypes.arrayOf(
    PropTypes.shape({
      TaskId: PropTypes.number,
      STRID: PropTypes.string,
      AlertDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      TriggerType: PropTypes.string,
      TriggerSource: PropTypes.string,
      Status: PropTypes.string,
      Priority: PropTypes.string,
      NumberOfActiveDays: PropTypes.number,
      OwnerName: PropTypes.string,
    })
  ),
};

export default TasksModalBase;